import { isITTL, ITTL } from "./ITTL";
import { publishSessionStorage } from "./googleTagManager";

const KEY_PREFIX = "gtm_";

/**
 * Module added to _Root. Should be initialized only once per a page load.
 */
export const gtmSessionStorage = () => {
  // Publish stored values to GTM.
  const gtmSessionStorageKeys = Object.keys(sessionStorage).filter(key =>
    key.startsWith(KEY_PREFIX)
  );

  const valuesForPublishing: Record<string, object> = {};

  for (const key of gtmSessionStorageKeys) {
    const json = sessionStorage.getItem(key);

    if (json) {
      const value = JSON.parse(json);

      valuesForPublishing[key.replace(KEY_PREFIX, "")] = getGtmValue(value);

      if (isITTL(value)) {
        handleTTLValue(key, value);
      }
    }
  }

  publishValues(valuesForPublishing);
};

const createSessionStorageKey = (key: string) => `${KEY_PREFIX}${key}`;

/**
 * Stores and publishes a GTM value.
 * @param key Value name.
 * @param value Value.
 */
export const storeValue = (key: string, value: object) => {
  sessionStorage.setItem(createSessionStorageKey(key), JSON.stringify(value));
  publishValue(key, value);
};

const publishValue = (key: string, value: object) => {
  const valueToPublish = getGtmValue(value);
  publishSessionStorage({ [key]: valueToPublish });
};

const publishValues = (values: Record<string, object>) => {
  const gtmValues = { ...values };
  for (const key in gtmValues) {
    gtmValues[key] = getGtmValue(gtmValues[key]);
  }

  publishSessionStorage(gtmValues);
};

/**
 * Gets a value to be published in GTM. Removes TTL property and flattens object is only 1 property is present.
 * @param value
 * @returns
 */
const getGtmValue = (value: object) => {
  let newValue = value;

  // If value is an object with TTL, don't publish the TTL value
  if (isITTL(value)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { TTL, ...rest } = value;
    newValue = rest;
  }

  // If newValue is a simple object with only one value, retrieve that value directly.
  if (Object.keys(newValue).length == 1) {
    return newValue[Object.keys(newValue)[0] as keyof object];
  }

  return newValue;
};

/**
 * Handles the TTL values - decrease counter and store or delete the updated value.
 * @param key Session storage key.
 * @param ttlObject TTL Object.
 */
const handleTTLValue = (key: string, ttlObject: ITTL) => {
  ttlObject.TTL -= 1;

  if (ttlObject.TTL > 0) {
    // Store value
    sessionStorage.setItem(key, JSON.stringify(ttlObject));
  } else {
    // Remove value
    sessionStorage.removeItem(key);
  }
};
